import React, { useEffect, useState } from 'react';
import { PageContainer } from '../../components/layout';
import { KpiCard, FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { DateRangePicker } from '../../components/admin/DateRangePicker';
import { adminStatisticsService } from '../../services/adminStatisticsService';
import type {
  StatisticsSummaryResponse,
  ClientStatsRow,
  ClientDomainStatsRow,
} from '../../types/adminStatistics';
import { toastError } from '../../lib/toast';

const computeVariation = (current: number, previous: number) => {
  if (previous === 0) return undefined;
  const value = Math.round(((current - previous) / previous) * 100);
  return { value, positive: value >= 0 };
};

const fmtUsd = (value: string | number) =>
  `$${Number(value).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (v: number) => `${Math.round(v * 100)}%`;

export const StatistiquesPage: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10);

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [includeSystem, setIncludeSystem] = useState(true);
  const [data, setData] = useState<StatisticsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Skip pendant que l'utilisateur tape une date (input date emet onChange
    // sur chaque caractere). Ne fetcher qu'une fois from/to complets et
    // chronologiques — sinon backend renvoie 422.
    const dateRe = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRe.test(from) || !dateRe.test(to) || from > to) return;
    // Backend impose 20 ans max (admin_statistics.py) — guard cote UI pour
    // eviter le spam de 422 quand l'utilisateur tape une annee tres ancienne.
    const spanDays = (new Date(to).getTime() - new Date(from).getTime()) / 86400000;
    if (spanDays > 365 * 20) {
      toastError('Période max : 20 ans');
      return;
    }
    setLoading(true);
    adminStatisticsService
      .getSummary({ from, to, includeSystem })
      .then(setData)
      .catch((err) => toastError('Erreur chargement statistiques', err))
      .finally(() => setLoading(false));
  }, [from, to, includeSystem]);

  const kpisCurrent = data?.kpis.client_production;
  const kpisPrev = data?.kpis_previous_period.client_production;
  const sysCurrent = data?.kpis.system;

  const byClientColumns: FilterableColumnDef<ClientStatsRow>[] = [
    { key: 'client_name', header: 'Client', sortable: true, sortValueFn: (r) => r.client_name, render: (r) => r.client_name },
    { key: 'docs_extracted', header: 'Docs ext.', sortable: true, sortValueFn: (r) => String(r.docs_extracted).padStart(12, '0'), render: (r) => r.docs_extracted },
    { key: 'llm_calls', header: 'LLM', sortable: true, sortValueFn: (r) => String(r.llm_calls).padStart(12, '0'), render: (r) => r.llm_calls },
    { key: 'reextraction_rate', header: '% réext.', sortable: true, sortValueFn: (r) => String(r.reextraction_rate), render: (r) => fmtPct(r.reextraction_rate) },
    { key: 'docs_validated', header: 'Validés', sortable: true, sortValueFn: (r) => String(r.docs_validated).padStart(12, '0'), render: (r) => r.docs_validated },
    { key: 'validation_rate', header: '% val.', sortable: true, sortValueFn: (r) => String(r.validation_rate), render: (r) => fmtPct(r.validation_rate) },
    { key: 'cost_usd', header: '$', sortable: true, sortValueFn: (r) => String(Number(r.cost_usd)).padStart(12, '0'), render: (r) => fmtUsd(r.cost_usd), align: 'right' },
  ];

  const byClientDomainColumns: FilterableColumnDef<ClientDomainStatsRow>[] = [
    { key: 'client_name', header: 'Client', sortable: true, sortValueFn: (r) => r.client_name, render: (r) => r.client_name },
    { key: 'domain_code', header: 'Domaine', sortable: true, sortValueFn: (r) => r.domain_code, render: (r) => r.domain_code },
    { key: 'docs_extracted', header: 'Docs ext.', sortable: true, sortValueFn: (r) => String(r.docs_extracted).padStart(12, '0'), render: (r) => r.docs_extracted },
    { key: 'llm_calls', header: 'LLM', sortable: true, sortValueFn: (r) => String(r.llm_calls).padStart(12, '0'), render: (r) => r.llm_calls },
    { key: 'cost_usd', header: '$', sortable: true, sortValueFn: (r) => String(Number(r.cost_usd)).padStart(12, '0'), render: (r) => fmtUsd(r.cost_usd), align: 'right' },
  ];

  return (
    <PageContainer>
      <h1 className="text-xl font-semibold text-gray-900 mb-4">Statistiques (Superadmin)</h1>

      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 rounded">
        <DateRangePicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeSystem}
            onChange={(e) => setIncludeSystem(e.target.checked)}
          />
          Inclure scope Système (Dokimo)
        </label>
      </div>

      <h2 className="text-lg font-semibold mb-3">Production client</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Docs extraits"
          value={kpisCurrent?.docs_extracted ?? '—'}
          variation={kpisCurrent && kpisPrev ? computeVariation(kpisCurrent.docs_extracted, kpisPrev.docs_extracted) : undefined}
          loading={loading}
        />
        <KpiCard
          label="Appels LLM"
          value={kpisCurrent?.llm_calls ?? '—'}
          variation={kpisCurrent && kpisPrev ? computeVariation(kpisCurrent.llm_calls, kpisPrev.llm_calls) : undefined}
          loading={loading}
        />
        <KpiCard
          label="Docs validés"
          value={kpisCurrent?.docs_validated ?? '—'}
          variation={kpisCurrent && kpisPrev ? computeVariation(kpisCurrent.docs_validated, kpisPrev.docs_validated) : undefined}
          loading={loading}
        />
        <KpiCard
          label="Coût LLM"
          value={kpisCurrent ? fmtUsd(kpisCurrent.cost_usd) : '—'}
          variation={kpisCurrent && kpisPrev ? computeVariation(Number(kpisCurrent.cost_usd), Number(kpisPrev.cost_usd)) : undefined}
          loading={loading}
        />
      </div>

      {includeSystem && sysCurrent && (
        <>
          <h2 className="text-lg font-semibold mb-3">Système (Dokimo)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
            <KpiCard label="Appels LLM" value={sysCurrent.llm_calls} loading={loading} />
            <KpiCard label="Tokens" value={sysCurrent.tokens_total.toLocaleString('fr-CA')} loading={loading} />
            <KpiCard label="Coût LLM" value={fmtUsd(sysCurrent.cost_usd)} loading={loading} />
          </div>
          {data && data.system_breakdown.length > 0 && (
            <div className="text-sm text-gray-600 mb-6">
              Détail : {data.system_breakdown.map((b) => `${b.source} ${b.llm_calls} (${fmtUsd(b.cost_usd)})`).join(' | ')}
            </div>
          )}
        </>
      )}

      <h2 className="text-lg font-semibold mb-3">Activité par client</h2>
      {data && (
        <FilterableTable<ClientStatsRow>
          persistKey="parametres-statistiques-by-client"
          data={data.by_client}
          columns={byClientColumns}
          rowKey={(r) => r.client_id}
          noDataMessage="Aucune activité client sur cette période."
        />
      )}

      <details className="mt-6">
        <summary className="cursor-pointer text-lg font-semibold mb-3">
          Activité par client × domaine
        </summary>
        {data && (
          <div className="mt-2">
            <FilterableTable<ClientDomainStatsRow>
              persistKey="parametres-statistiques-by-client-domain"
              data={data.by_client_domain}
              columns={byClientDomainColumns}
              rowKey={(r) => `${r.client_id}:${r.domain_code}`}
              noDataMessage="Aucune activité client × domaine sur cette période."
            />
          </div>
        )}
      </details>

      <h2 className="text-lg font-semibold mt-6 mb-3">Top 5 validateurs</h2>
      {data && (
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left p-2">Utilisateur</th>
              <th className="text-left p-2">Validations</th>
              <th className="text-left p-2">Clients</th>
            </tr>
          </thead>
          <tbody>
            {data.top_validators.map((v) => (
              <tr key={v.user_id} className="border-t">
                <td className="p-2">{v.user_name}</td>
                <td className="p-2">{v.validation_count}</td>
                <td className="p-2">{v.client_names.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PageContainer>
  );
};

export default StatistiquesPage;
